/**
 * v4.39 - 2026-10-31 - BATCH-2026-10-31 row 40 (option A2): "Skip for now" saves the note (POST
 *                      /api/onboarding/connect-skip) and then loads /dashboard in full, so /api/me is read again and the
 *                      dashboard shows the Connect your data banner. A failed save shows an error on this step and stays
 *                      (no navigation, so no loop). Before, Skip only moved to step 4 and a reload came back here.
 * v4.28 - 2026-10-13 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): the proposal review dialog shows its actions only with reviewProposals
 * Onboarding Page - Multi-step wizard for new users
 *
 * Version History:
 * v4.26 - 2026-10-06 - INDUSTRY-CODE-NOT-STORED (S5, S7, S8; F1, F7, F8, F11): the industry dialog no longer
 *                       depends on a build switch; it opens whenever basic-info stored no code, and is restored from
 *                       the server (GET /api/onboarding/naics-status) on load and past Your Info; a company that
 *                       skipped company basics is sent back to Your Info; no Skip on company basics; Finish Setup /
 *                       Retry answered INDUSTRY_CODE_MISSING open the same screen (onNeedIndustry)
 * v3.67 - 2026-03-10 - NAICS-03: Feature flag REACT_APP_NAICS_CONFIRM_DIALOG_ENABLED to skip dialog for pilot
 * v3.63 - 2026-03-07 - Bug 2b: route guard — redirect to / if tenantId not provisioned (captive-path enforcement)
 * v3.61 - 2026-03-06 - DEMO-01: Render DemoCTA when demo tenant reaches setup_complete
 * v3.60 - 2026-03-06 - NAICS-01: Wire in NAICSConfirmationDialog for auditor confidence < 0.80
 * v3.13 - 2026-02-14 - Fix #6: ProposalReview as Dialog overlay (animation stays mounted)
 *                       Fix #11/#12: hasResumedRef prevents stale workflowState prop re-triggering review loop
 * v2.88 - 2026-02-06 - Added ProposalReview flow, awaiting_approval handling, fixed dashboard redirect
 * v2.87 - 2026-02-05 - Push TWO history entries for back button trap (single entry escaped on double-click)
 * v2.86 - 2026-02-05 - Browser back button trap: pushState + popstate listener when pipeline is active
 * v2.82 - 2026-02-05 - Sync pipelineRunning state from props changes (handles refresh/prop updates)
 * v2.79 - 2026-02-05 - Added captiveComplete prop: "in_progress" locks on step 4, "done" redirects to dashboard
 * v2.78 - 2026-02-05 - Lock user on pipeline progress step when pipeline is running (back button guard)
 * v2.55 - 2026-02-04 - Migrated to Clerk authentication (useClerk signOut)
 * v2.53 - 2026-02-03 - Added BasicInfoForm step for company name, role, industry before discovery
 * v2.46 - 2026-02-03 - Handle OAuth success redirect, resume pipeline_running state
 * v2.22 - 2026-01-25 - Resume at correct step based on workflowState prop
 * v2.14 - 2026-01-24 - Push TWO states + redirect to home on popstate
 * v2.13 - 2026-01-24 - Back button trap moved to index.html (runs before React)
 * v2.12 - 2026-01-24 - Global back button trap in App.tsx runs on app load
 * v2.11 - 2026-01-24 - Fixed back button trap in App.tsx
 * v2.10 - 2026-01-24 - History cleanup in App.tsx (insufficient fix)
 * v2.9  - 2026-01-24 - Added /logged-out page to handle browser back button after logout
 * v2.8  - 2026-01-24 - Fixed logout to use replace() preventing back button issue
 * v2.7  - 2026-01-23 - Added header with logout, improved Back button styling
 * v2.6  - 2026-01-23 - BFF pattern for PipelineProgress
 * v2.5  - 2026-01-23 - BFF pattern for DiscoveryChat
 * v2.2  - 2026-01-23 - Initial implementation
 */
// v2.55 - Added Clerk signOut for logout
import React, { useState, useEffect, useCallback } from 'react';
import MyLojicLogo from '../components/MyLojicLogo';
import { useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/clerk-react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  LinearProgress,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogContent,
  Alert,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import DiscoveryChat from '../components/DiscoveryChat';
import DataSourceConnect from '../components/DataSourceConnect';
import PipelineProgress from '../components/PipelineProgress';
import ProposalReview from '../components/ProposalReview';
import { usePermissions } from '../hooks/usePermissions'; // v4.28 - G10-B
import BasicInfoForm from '../components/BasicInfoForm';
// v3.61 - DEMO-01: CTA screen for demo tenants reaching setup_complete
import DemoCTA from '../components/DemoCTA';
import { onboardingService } from '../services/api'; // v4.39 - BATCH-2026-10-31 row 40: connect-skip

/**
 * v4.39 - BATCH-2026-10-31 row 40: save the "skipped for now" note, then a full page load of the dashboard (so /api/me
 * is read again and the first-screen rule sees the note). Any failure: no navigation; the caller shows the error on the
 * Connect your data step. Pure (the API call and the navigation are passed in) so it is testable without a DOM.
 */
export async function runConnectSkip(
  saveNote: () => Promise<{ connectSkipped: boolean; requestId?: string }>,
  loadPage: (url: string) => void,
): Promise<{ ok: boolean; requestId?: string }> {
  try {
    const res = await saveNote();
    if (!res || res.connectSkipped !== true) return { ok: false, requestId: res?.requestId };
    loadPage('/dashboard');
    return { ok: true, requestId: res.requestId };
  } catch {
    return { ok: false };
  }
}

// v11.13 - 2026-10-28 - ONBOARDING-INDUSTRY-PICKER (Branko M31077): the
// NAICSConfirmationDialog + onboardingService.naicsStatus wiring are retired.
// The customer picks their industry from the three dropdowns + popup inside
// BasicInfoForm (step 1). When a later step reports INDUSTRY_CODE_MISSING,
// handleNeedIndustry below sends the customer back to that same step; there
// is no overlay dialog and no code-picking screen anywhere.

// v2.53 - Added 'Your Info' step before Discovery
const steps = ['Welcome', 'Your Info', 'Discovery', 'Connect Data', 'Complete'];

interface OnboardingProps {
  tenantId: string;
  email: string;
  workflowState?: string; // v2.22 - Added to resume at correct step
  captiveComplete?: string; // v2.79 - Captive onboarding flag
  isDemo: boolean; // v4.11 - supplied by the single parent auth state; never create a nested auth hook
}

const Onboarding: React.FC<OnboardingProps> = ({ tenantId, email, workflowState, captiveComplete, isDemo }) => {
  // v4.28 - G10-B (UI-PERMISSION-LIST-NOT-ENFORCED-01): reviewing proposals follows the server's permission map
  const { hasPermission } = usePermissions();
  const canReviewProposals = hasPermission('reviewProposals');
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { signOut } = useClerk(); // v2.55 - Clerk signOut

  // v2.46 - Check URL params for OAuth success redirect
  const urlParams = new URLSearchParams(window.location.search);
  const oauthSuccess = urlParams.get('success');

  // v2.22 - Calculate initial step based on workflowState
  // v2.46 - Also check for pipeline_running state and OAuth success
  // v2.53 - Updated step numbers for new 'Your Info' step
  const getInitialStep = () => {
    // v2.79 - If captive in_progress, always start at step 4 (pipeline progress)
    if (captiveComplete === 'in_progress') {
      return 4;
    }
    // v2.88 - If awaiting_approval, go to step 4 (will show ProposalReview)
    if (workflowState === 'awaiting_approval') {
      return 4;
    }
    // If pipeline is already running, go to step 4 (Complete/Pipeline)
    if (workflowState === 'pipeline_running' || workflowState === 'setup_complete') {
      return 4; // Complete/Pipeline step
    }
    // If OAuth just completed successfully, go to step 3 (Connect Data) to show Finish Setup
    if (oauthSuccess) {
      return 3; // Connect Data step - will show connected source + Finish Setup button
    }
    if (workflowState === 'discovery_complete' || workflowState === 'connections_ready') {
      return 3; // Connect Data step
    }
    // v2.53 - If basic info already collected, skip to discovery
    if (workflowState === 'basic_info_complete' || workflowState === 'discovery_in_progress') {
      return 2; // Discovery step
    }
    return 0; // Welcome step
  };

  const [activeStep, setActiveStep] = useState(getInitialStep());
  // v2.53 - Track basic info completion
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [basicInfoComplete, setBasicInfoComplete] = useState(
    workflowState === 'basic_info_complete' ||
    workflowState === 'discovery_in_progress' ||
    workflowState === 'discovery_complete' ||
    workflowState === 'connections_ready' ||
    workflowState === 'pipeline_running' ||
    !!oauthSuccess
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [discoveryComplete, setDiscoveryComplete] = useState(
    workflowState === 'discovery_complete' ||
    workflowState === 'connections_ready' ||
    workflowState === 'pipeline_running' ||
    !!oauthSuccess
  );
  const [dataSourceConnected, setDataSourceConnected] = useState(!!oauthSuccess);
  // v2.44 - pipelineRunning controls showing PipelineProgress (no longer need jobId)
  // v2.46 - Check if pipeline is already running from workflowState
  // v2.88 - Also check awaiting_approval as pipeline-active state
  const [pipelineRunning, setPipelineRunning] = useState(
    workflowState === 'pipeline_running' || workflowState === 'awaiting_approval' || captiveComplete === 'in_progress'
  );
  // v2.88 - Show ProposalReview when awaiting_approval
  const [showProposalReview, setShowProposalReview] = useState(
    workflowState === 'awaiting_approval'
  );
  // v11.13 - 2026-10-28 - ONBOARDING-INDUSTRY-PICKER: the dialog state (and
  // the recovery/originRef machinery it needed) was retired with the dialog
  // itself. INDUSTRY_CODE_MISSING is now resolved by sending the customer
  // back to step 1 (Your Info), where BasicInfoForm renders the dropdowns
  // and the match popup.

  // v3.13 - Fix #11/#12: Track whether user has already resumed once (prevents stale prop re-trigger)
  const hasResumedRef = React.useRef(false);

  // v2.88 - Sync pipelineRunning and showProposalReview state from props when they change
  // v3.13 - Fix #11/#12: Once user has resumed, ignore the stale workflowState prop entirely.
  // The prop comes from App.tsx's single /api/me fetch and is never updated.
  useEffect(() => {
    if ((workflowState === 'pipeline_running' || workflowState === 'awaiting_approval' || captiveComplete === 'in_progress') && !pipelineRunning) {
      console.log('v2.88: Props indicate pipeline running, updating state:', { workflowState, captiveComplete });
      setPipelineRunning(true);
    }
    // v3.13 - Only show proposal review from prop if user hasn't already resumed
    if (workflowState === 'awaiting_approval' && !showProposalReview && !hasResumedRef.current) {
      console.log('v3.13: First-time awaiting_approval from prop, showing proposal review');
      setShowProposalReview(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowState, captiveComplete]);

  // v2.79 - Lock user on pipeline progress step when captive or pipeline is running (back button guard)
  useEffect(() => {
    if ((pipelineRunning || captiveComplete === 'in_progress') && activeStep !== 4) {
      console.log('v2.82: Forcing step 4 (pipeline active):', { pipelineRunning, captiveComplete, activeStep });
      setActiveStep(4);
    }
  }, [pipelineRunning, captiveComplete, activeStep]);

  // v2.87 - Browser back button trap when pipeline is active
  // Push TWO dummy history entries so rapid back-button clicks can't escape.
  // On popstate, re-push two to maintain the buffer.
  useEffect(() => {
    if (!pipelineRunning && captiveComplete !== 'in_progress') return;

    // Push two dummy entries — buffer against rapid back clicks
    window.history.pushState({ captive: true }, '', window.location.href);
    window.history.pushState({ captive: true }, '', window.location.href);

    const handlePopState = () => {
      // User pressed back — re-push two entries to keep them trapped
      console.log('v2.87: Back button trapped — staying on pipeline progress');
      window.history.pushState({ captive: true }, '', window.location.href);
      window.history.pushState({ captive: true }, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [pipelineRunning, captiveComplete]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // v2.55 - Use Clerk's signOut method
  const handleLogout = async () => {
    await signOut();
    navigate('/logged-out', { replace: true });
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  // v11.13 - 2026-10-28 - ONBOARDING-INDUSTRY-PICKER (Branko M31077): when a
  // later step reports INDUSTRY_CODE_MISSING (pipeline readiness), send the
  // customer back to step 1 (Your Info). The three dropdowns and the
  // IndustryMatchPicker popup inside BasicInfoForm are the only paths that
  // ever record a code. No overlay dialog; no code screen.
  const handleNeedIndustry = useCallback(() => {
    setActiveStep((prev) => (prev === 1 ? prev : 1));
  }, []);

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  // v11.13 - BasicInfoForm now stores the picked 6-digit code itself via
  // /api/onboarding/basic-info. Onboarding just advances to Discovery once
  // the form reports success.
  const handleBasicInfoComplete = () => {
    setBasicInfoComplete(true);
    handleNext();
  };

  const handleDiscoveryComplete = () => {
    setDiscoveryComplete(true);
    handleNext();
  };

  // v2.44 - Updated for pipeline orchestration (no jobId needed)
  const handleDataSourceConnected = () => {
    setDataSourceConnected(true);
    setPipelineRunning(true);
    handleNext();
  };

  // v4.39 - BATCH-2026-10-31 row 40: save the note, then a full page load of the dashboard; on failure stay here.
  const [connectSkipError, setConnectSkipError] = useState<string | null>(null);
  const handleSkipDataSource = async () => {
    setConnectSkipError(null);
    const result = await runConnectSkip(onboardingService.connectSkip, (url) => { window.location.href = url; });
    if (!result.ok) setConnectSkipError(t('steps.dataSource.skipFailed'));
  };

  // v3.00 - Full page reload with setup=complete param to bypass stale Cosmos read.
  // The orchestrator writes setup_complete to Cosmos via d75-agent, but /api/me reads
  // from b2c-webhook (different Cosmos session) which may return stale pipeline_running.
  // The URL param signals App.tsx to skip the captive guard for this navigation.
  const handleGoToDashboard = () => {
    window.location.href = '/dashboard?setup=complete';
  };

  // v2.88 - Handler for PipelineProgress to navigate to proposal review
  // v3.11c - Skip if we just resumed (prevents loop from stale poll data)
  // v3.13 - Fix #11/#12: Also block navigate-to-review if user already resumed once
  const handleNavigateToReview = () => {
    if (hasResumedRef.current) {
      console.log('v3.13: Ignoring navigate-to-review — user already resumed');
      return;
    }
    const sinceResume = Date.now() - resumedAtRef.current;
    if (sinceResume < 15000) {
      console.log('v3.11c: Ignoring navigate-to-review within 15s of resume');
      return;
    }
    console.log('v2.88: Navigating to proposal review');
    setShowProposalReview(true);
  };

  // v2.88 - Handler for ProposalReview to resume pipeline after approval
  // v3.11c - Track resume timestamp to prevent loop back to review
  const resumedAtRef = React.useRef<number>(0);
  const handleResumeAfterReview = () => {
    console.log('v3.13: Resuming pipeline after proposal review (hasResumed=true)');
    resumedAtRef.current = Date.now();
    hasResumedRef.current = true;  // v3.13 - Fix #11/#12: Prevent stale prop from re-triggering review
    setShowProposalReview(false);
    // Pipeline will resume and PipelineProgress will continue polling
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
              {t('steps.welcome.title')}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 4, maxWidth: 500, mx: 'auto' }}>
              {t('steps.welcome.description')}
            </Typography>
            <Box sx={{
              width: 120,
              height: 120,
              bgcolor: 'primary.light',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 4
            }}>
              <Typography variant="h2">👋</Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              onClick={handleNext}
              sx={{
                bgcolor: 'accent.main',
                color: 'white',
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': { bgcolor: 'accent.dark' }
              }}
            >
              {t('actions.next')}
            </Button>
          </Box>
        );

      // v2.53 - New step: Basic Info Form
      case 1:
        return (
          // v4.26 - INDUSTRY-CODE-NOT-STORED S7 (F7): no Skip on company basics (BasicInfoForm renders Skip only
          // when onSkip is passed)
          <BasicInfoForm
            onComplete={handleBasicInfoComplete}
          />
        );

      case 2:
        return (
          <DiscoveryChat
            onComplete={handleDiscoveryComplete}
          />
        );

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 700, mb: 1, textAlign: 'center' }}>
              {t('steps.dataSource.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, textAlign: 'center' }}>
              {t('steps.dataSource.description')}
            </Typography>
            {connectSkipError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setConnectSkipError(null)}>
                {connectSkipError}
              </Alert>
            )}
            <DataSourceConnect
              tenantId={tenantId}
              onConnect={handleDataSourceConnected}
              onSkip={handleSkipDataSource}
              onNeedIndustry={handleNeedIndustry} // v11.13 - ONBOARDING-INDUSTRY-PICKER: go back to Your Info
            />
          </Box>
        );

      case 4:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            {/* v3.13 - Fix #6: ProposalReview renders as Dialog overlay, PipelineAnimation stays mounted */}
            {pipelineRunning ? (
              <>
                <PipelineProgress
                  onComplete={handleGoToDashboard}
                  onNavigateToReview={handleNavigateToReview}
                  onNeedIndustry={handleNeedIndustry} // v11.13 - ONBOARDING-INDUSTRY-PICKER: go back to Your Info
                />
                {/* v3.13 - Fix #6: Dialog overlay keeps animation visible underneath */}
                <Dialog
                  open={showProposalReview}
                  onClose={() => {}}
                  maxWidth="lg"
                  fullWidth
                  disableEscapeKeyDown
                  PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 2 } }}
                >
                  <DialogContent sx={{ p: 0 }}>
                    {canReviewProposals ? (
                      <ProposalReview onResume={handleResumeAfterReview} />
                    ) : (
                      <Alert severity="info" sx={{ m: 3 }}>{t('teamAccess.noReviewPermission', { ns: 'common' })}</Alert>
                    )}
                  </DialogContent>
                </Dialog>
              </>
            ) : (
              <>
                <Box sx={{
                  width: 100,
                  height: 100,
                  bgcolor: 'success.light',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3
                }}>
                  <Typography variant="h2">✓</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700, mb: 2 }}>
                  {t('complete.title')}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                  {dataSourceConnected ? t('complete.withData') : t('complete.withoutData')}
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGoToDashboard}
                  sx={{
                    bgcolor: 'accent.main',
                    color: 'white',
                    px: 6,
                    py: 1.5,
                    '&:hover': { bgcolor: 'accent.dark' }
                  }}
                >
                  {t('actions.goToDashboard')}
                </Button>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  // v3.63 - Bug 2b: /onboarding is captive-only. Direct URL without provisioned tenantId → redirect /.
  // tenantId is '' when App.tsx has no tenant yet (not provisioned or arrived without /auth-landing).
  useEffect(() => {
    if (!tenantId) {
      navigate('/', { replace: true });
    }
  }, [tenantId, navigate]);

  // Return null immediately if not provisioned (prevents flash of onboarding UI)
  if (!tenantId) return null;

  // v3.61 - DEMO-01: show CTA screen for demo tenants when pipeline completes (one code path)
  if (isDemo && workflowState === 'setup_complete') {
    return <DemoCTA />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header with Logo and Logout */}
      <AppBar position="static" sx={{ bgcolor: 'secondary.main' }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <MyLojicLogo isDark={true} height={36} />
          </Box>
          <IconButton
            color="inherit"
            onClick={handleMenuOpen}
            sx={{ ml: 2 }}
          >
            <AccountCircleIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* v4.10 - Responsive container and paper padding for mobile */}
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2 }}>
          {/* Progress Stepper — alternativeLabel keeps it compact on mobile */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: { xs: 2, md: 4 } }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content */}
          {renderStepContent(activeStep)}

          {/* Navigation Buttons - Show back on steps 1 and 2 */}
          {activeStep > 0 && activeStep < 3 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 4 }}>
              <Button
                onClick={handleBack}
                variant="outlined"
                size="large"
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  px: 4,
                  py: 1,
                  '&:hover': {
                    bgcolor: 'primary.light',
                    borderColor: 'primary.dark',
                    color: 'white',
                  },
                }}
              >
                {t('actions.back')}
              </Button>
            </Box>
          )}
        </Paper>
      </Container>

      {/* v11.13 - 2026-10-28 - ONBOARDING-INDUSTRY-PICKER: the confirmation
          dialog overlay was retired. The customer picks their industry via
          the three dropdowns + match popup inside BasicInfoForm (step 1).
          handleNeedIndustry above sends them back here when a later step
          reports INDUSTRY_CODE_MISSING. */}
    </Box>
  );
};

export default Onboarding;
